/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import axios, { AxiosError, AxiosRequestConfig } from "axios"
import { InteractionRequiredAuthError } from "@azure/msal-browser"
import { requestToken } from "../../auth/AuthServices"
// @ts-ignore - JS file without types
import { loginRequest, msalInstance } from "../../config/api.config"
import {
  hets_service_endpoint,
  master_v2_service_endpoint,
  sap_service_endpoint,
  wellness_endpoint,
} from "../../config/api.config"

type QueueItem = {
  resolve: (token: string) => void
  reject: (err: unknown) => void
}

let isRefreshing = false
let pendingQueue: QueueItem[] = []

const flushQueue = (token: string | null, err?: unknown) => {
  pendingQueue.forEach(p => {
    if (token) p.resolve(token)
    else p.reject(err)
  })
  pendingQueue = []
}

const OFFLINE_TOKEN_KEY = "offline_access_token"
const OFFLINE_TOKEN_EXPIRY_KEY = "offline_token_expiry"

const storeOfflineToken = (token: string, expiresOn?: Date | null) => {
  try {
    localStorage.setItem(OFFLINE_TOKEN_KEY, token)
    if (expiresOn) {
      localStorage.setItem(OFFLINE_TOKEN_EXPIRY_KEY, expiresOn.getTime().toString())
    }
  } catch (e) {
    console.error("Failed to store offline token", e)
  }
}

const refreshAccessToken = async (): Promise<string> => {
  const accounts = msalInstance.getAllAccounts()
  if (!accounts || accounts.length === 0) {
    throw new InteractionRequiredAuthError("no_account", "No active MSAL account")
  }
  const result = await msalInstance.acquireTokenSilent({
    ...loginRequest,
    account: msalInstance.getActiveAccount() || accounts[0],
    forceRefresh: true,
  })
  storeOfflineToken(result.accessToken, result.expiresOn)
  return result.accessToken
}

const api = (baseUrl: string) => {
  const instance = axios.create({
    baseURL: baseUrl,
    headers: {
      "Content-Type": "application/json",
      "x-api-key": process.env.REACT_APP_FARMS_BACKEND_API_KEY || "",
    },
  })

  instance.interceptors.request.use(
    async config => {
      const token = await requestToken(false)
      if (token) {
        config.headers["x-access-token"] = token
      }
      return config
    },
    error => {
      return Promise.reject(error)
    }
  )

  instance.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined

      if (!originalRequest || error.response?.status !== 401 || originalRequest._retry) {
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({
            resolve: (token: string) => {
              originalRequest._retry = true
              originalRequest.headers = {
                ...(originalRequest.headers || {}),
                "x-access-token": token,
              }
              resolve(instance(originalRequest))
            },
            reject,
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const newToken = await refreshAccessToken()
        flushQueue(newToken)
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          "x-access-token": newToken,
        }
        return instance(originalRequest)
      } catch (refreshErr) {
        flushQueue(null, refreshErr)
        if (refreshErr instanceof InteractionRequiredAuthError) {
          try {
            await msalInstance.loginRedirect(loginRequest)
          } catch (e) {
            console.error("loginRedirect failed", e)
          }
        }
        return Promise.reject(refreshErr)
      } finally {
        isRefreshing = false
      }
    }
  )

  return instance
}

export const sapApi = api(sap_service_endpoint.endpoint || "")
export const hetsApi = api(hets_service_endpoint.endpoint || "")
export const masterV2Api = api(master_v2_service_endpoint.endpoint || "")
export const wellnessApi = api(wellness_endpoint.endpoint || "")
